import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { ScrollArea } from '../components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { cn } from '../lib/utils';
import { toast } from 'sonner';
import { FogOverlay } from '../components/maps/FogOverlay';
import { TacticalGridOverlay } from '../components/maps/TacticalGridOverlay';
import Dice3DOverlay from '../components/dice/Dice3DOverlay';
import { useRemoteDiceRoll } from '../components/dice/useRemoteDiceRoll';
import { 
  Map as MapIcon,
  MapPin,
  Loader2,
  ZoomIn,
  ZoomOut,
  Maximize2,
  Minimize2,
  Navigation,
  X,
  Castle,
  Mountain,
  Skull,
  Gem,
  Flag,
  AlertTriangle,
  Star,
  ChevronRight,
  Home,
  Compass,
  Link2,
  Users,
  Scroll,
  Book,
  Eye,
  RefreshCw
} from 'lucide-react';

const API_URL = '/api';

// Pin type configuration
const PIN_TYPES = [
  { id: 'city', label: 'Ville', icon: Castle, color: '#3B82F6' },
  { id: 'dungeon', label: 'Donjon', icon: Mountain, color: '#6B7280' },
  { id: 'quest', label: 'Quete', icon: Star, color: '#F59E0B' },
  { id: 'danger', label: 'Danger', icon: Skull, color: '#EF4444' },
  { id: 'treasure', label: 'Tresor', icon: Gem, color: '#10B981' },
  { id: 'faction', label: 'Faction', icon: Flag, color: '#8B5CF6' },
  { id: 'event', label: 'Evenement', icon: AlertTriangle, color: '#EC4899' },
  { id: 'portal', label: 'Portail', icon: Navigation, color: '#06B6D4' },
  { id: 'custom', label: 'Autre', icon: MapPin, color: '#6366F1' }
];

const getPinIcon = (pinType) => PIN_TYPES.find(p => p.id === pinType)?.icon || MapPin;
const getPinColor = (pinType) => PIN_TYPES.find(p => p.id === pinType)?.color || '#6366F1';

export default function PlayerMapViewPage() {
  const { token } = useParams();
  
  // Data state
  const [map, setMap] = useState(null);
  const [pins, setPins] = useState([]);
  const [availableMaps, setAvailableMaps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewStatus, setViewStatus] = useState('active'); // 'active' | 'idle'
  const [defaultImage, setDefaultImage] = useState(null);
  const [campaignName, setCampaignName] = useState('');
  const { activeRoll, observeRoll, clearActiveRoll } = useRemoteDiceRoll(token);
  
  // UI state
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showUI, setShowUI] = useState(true);
  
  // Pin detail
  const [selectedPin, setSelectedPin] = useState(null);
  const [pinDetail, setPinDetail] = useState(null);
  const [pinDetailOpen, setPinDetailOpen] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  
  // Sync state
  const [syncEnabled, setSyncEnabled] = useState(false);
  const [syncState, setSyncState] = useState(null);
  
  // Map navigation
  const [showMapList, setShowMapList] = useState(false);
  
  // Tactical Grid
  const [gridEnabled, setGridEnabled] = useState(false);
  const [gridSettings, setGridSettings] = useState({
    size: 50, color: '#ffffff', opacity: 0.3, type: 'square', offset_x: 0, offset_y: 0
  });

  // Fog of War - Player view uses black opaque fog
  const [fogEnabled, setFogEnabled] = useState(false);
  const [fogSettings, setFogSettings] = useState({
    opacity: 1.0,  // Full opacity for player view
    color: '#000000',  // Black fog
    blur: 15  // Softer edges
  });
  const [fogRevealedZones, setFogRevealedZones] = useState([]);
  
  const containerRef = useRef(null);
  const lastTouchDistanceRef = useRef(null);
  const syncIntervalRef = useRef(null);
  const mapIdRef = useRef(null);

  const applyLiveSnapshot = useCallback((snapshot, options = {}) => {
    if (!snapshot) return;
    const { applySyncedTransform = true } = options;
    observeRoll(snapshot.latest_public_roll || null);

    // Handle idle state (no map displayed)
    if (snapshot.status === 'idle' || !snapshot.map) {
      setViewStatus('idle');
      setMap(null);
      setPins([]);
      setAvailableMaps([]);
      setDefaultImage(snapshot.default_image || null);
      setCampaignName(snapshot.campaign_name || '');
      mapIdRef.current = null;
      return;
    }

    setViewStatus('active');
    setDefaultImage(null);
    const nextMap = snapshot.map;

    if (nextMap) {
      const previousMapId = mapIdRef.current;
      setMap(nextMap);
      mapIdRef.current = nextMap.id;
      if (previousMapId && previousMapId !== nextMap.id) {
        setSelectedPin(null);
        setPinDetail(null);
        setPinDetailOpen(false);
      }
    }

    setPins(snapshot.pins || []);
    setAvailableMaps(snapshot.available_maps || []);

    // Tactical Grid
    const grid = snapshot.grid || {};
    setGridEnabled(Boolean(grid.enabled));
    if (grid.settings) {
      setGridSettings(grid.settings);
    }

    const fog = snapshot.fog || {};
    setFogEnabled(Boolean(fog.enabled));
    // Always use black opaque fog for player view (ignore MJ settings)
    setFogSettings({
      opacity: 1.0,
      color: '#000000',
      blur: 15
    });
    setFogRevealedZones(fog.revealed_zones || []);

    const newSync = snapshot.sync || {};
    setSyncState(newSync);
    if (newSync.enabled) {
      setSyncEnabled(true);
      if (applySyncedTransform) {
        setPan({ x: newSync.pan_x ?? 0, y: newSync.pan_y ?? 0 });
        setZoom(newSync.zoom ?? 1);
      }
    } else {
      setSyncEnabled(false);
    }
  }, [observeRoll]);

  const fetchLiveData = useCallback(async (options = {}) => {
    const { setLoadError = false, applySyncedTransform = true, silent = false } = options;
    try {
      const response = await axios.get(`${API_URL}/player-view/map/${token}/live`);
      applyLiveSnapshot(response.data, { applySyncedTransform });
      if (setLoadError) {
        setError(null);
      }
      return response.data;
    } catch (err) {
      if (setLoadError) {
        setError('Carte introuvable ou lien expirée');
      } else if (!silent) {
        console.error('Error fetching player view live data');
      }
      return null;
    }
  }, [applyLiveSnapshot, token]);

  const reloadPlayerDisplay = async () => {
    const snapshot = await fetchLiveData({ applySyncedTransform: true, setLoadError: true, silent: true });
    if (snapshot) {
      toast.success('Affichage rechargé');
      return;
    }
    toast.error('Impossible de recharger l\'affichage');
  };

  // Initial load
  useEffect(() => {
    let isMounted = true;

    const loadData = async () => {
      setLoading(true);
      const snapshot = await fetchLiveData({ setLoadError: true, applySyncedTransform: true, silent: true });
      if (isMounted && !snapshot) {
        setMap(null);
      }
      if (isMounted) {
        setLoading(false);
      }
    };

    loadData();

    return () => {
      isMounted = false;
    };
  }, [fetchLiveData, token]);

  // SSE with polling fallback for map updates (runs even in idle state)
  useEffect(() => {
    let interval = null;
    let es = null;

    const pollData = async () => {
      if (document.hidden) return;
      await fetchLiveData({ applySyncedTransform: true, silent: true });
    };

    try {
      es = new EventSource(`/api/player-view/map/${token}/sse`);
      es.addEventListener('update', () => pollData());
      es.onerror = () => {
        es.close();
        es = null;
        if (!interval) interval = setInterval(pollData, 5000);
      };
    } catch {
      interval = setInterval(pollData, 5000);
    }

    return () => {
      if (es) es.close();
      if (interval) clearInterval(interval);
    };
  }, [fetchLiveData, token]);

  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (!document.hidden) {
        await fetchLiveData({ applySyncedTransform: true, silent: true });
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [fetchLiveData]);

  // Fetch pin detail
  const fetchPinDetail = async (pin) => {
    setLoadingDetail(true);
    try {
      const response = await axios.get(`${API_URL}/player-view/pin/${token}/${pin.id}`);
      setPinDetail(response.data);
    } catch (err) {
      toast.error('Erreur lors du chargement des details');
    } finally {
      setLoadingDetail(false);
    }
  };

  // Handle pin click
  const handlePinClick = (pin) => {
    setSelectedPin(pin);
    fetchPinDetail(pin);
    setPinDetailOpen(true);
  };

  // Navigate to linked map via pin
  const handleNavigateToMap = (mapId) => {
    // Find if this map is in available maps
    const targetMap = availableMaps.find(m => m.id === mapId);
    if (targetMap) {
      // Reload with same token but for different map
      // In a real scenario, you'd need a way to get the token for the other map
      // For now, we'll show a message that the MJ needs to open that map
      toast.info('Cette carte est liee. Demandez au MJ de l\'ouvrir en vue joueur.');
    }
  };

  // Mouse handlers
  const handleMouseDown = (e) => {
    if (syncEnabled) return; // Don't allow manual control in sync mode
    setIsDragging(true);
    setDragStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
  };

  const handleMouseMove = (e) => {
    if (isDragging && !syncEnabled) {
      setPan({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Touch handlers
  const handleTouchStart = (e) => {
    if (syncEnabled) return;
    if (e.touches.length === 2) {
      const dist = Math.hypot(
        e.touches[0].clientX - e.touches[1].clientX,
        e.touches[0].clientY - e.touches[1].clientY
      );
      lastTouchDistanceRef.current = dist;
    } else if (e.touches.length === 1) {
      const touch = e.touches[0];
      setDragStart({ x: touch.clientX - pan.x, y: touch.clientY - pan.y });
      setIsDragging(true);
    }
  };

  const handleTouchMove = (e) => {
    if (syncEnabled) return;
    if (e.touches.length === 2) {
      e.preventDefault();
      const dist = Math.hypot(
        e.touches[0].clientX - e.touches[1].clientX,
        e.touches[0].clientY - e.touches[1].clientY
      );
      if (lastTouchDistanceRef.current) {
        const scale = dist / lastTouchDistanceRef.current;
        setZoom(z => Math.min(3, Math.max(0.5, z * scale)));
      }
      lastTouchDistanceRef.current = dist;
    } else if (e.touches.length === 1 && isDragging) {
      const touch = e.touches[0];
      setPan({
        x: touch.clientX - dragStart.x,
        y: touch.clientY - dragStart.y
      });
    }
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
    lastTouchDistanceRef.current = null;
  };

  // Wheel zoom
  const handleWheel = useCallback((e) => {
    if (syncEnabled) return;
    e.preventDefault();
    const delta = e.deltaY > 0 ? -0.1 : 0.1;
    setZoom(z => Math.min(3, Math.max(0.5, z + delta)));
  }, [syncEnabled]);

  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      container.addEventListener('wheel', handleWheel, { passive: false });
      return () => container.removeEventListener('wheel', handleWheel);
    }
  }, [handleWheel]);

  // Fullscreen toggle
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  // Recenter map
  const recenter = () => {
    if (!syncEnabled) {
      setPan({ x: 0, y: 0 });
      setZoom(1);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Chargement de la carte...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="max-w-md">
          <CardHeader>
            <CardTitle className="text-destructive flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" />
              Erreur
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (viewStatus === 'idle' || !map) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center overflow-hidden relative">
        {defaultImage ? (
          <>
            <img
              src={defaultImage}
              alt={campaignName || 'Campagne'}
              className="absolute inset-0 w-full h-full object-cover"
            />
            {campaignName && (
              <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10">
                <Badge variant="secondary" className="bg-background/80 backdrop-blur-sm shadow-lg text-lg px-6 py-2 font-cinzel">
                  {campaignName}
                </Badge>
              </div>
            )}
          </>
        ) : (
          <div className="text-center">
            <MapIcon className="h-20 w-20 mx-auto text-muted-foreground/30 mb-6" />
            <h1 className="text-2xl font-bold text-foreground mb-2">
              {campaignName || 'Aucune carte affichée'}
            </h1>
            <p className="text-muted-foreground">
              Le MJ n'a pas encore sélectionné de carte à afficher.
            </p>
            <p className="text-xs text-muted-foreground mt-4 opacity-60">
              Mise à jour automatique
            </p>
          </div>
        )}
        {/* Discreet refresh button */}
        <button
          onClick={() => fetchLiveData({ applySyncedTransform: true, setLoadError: true, silent: true })}
          className="absolute bottom-4 right-4 z-10 p-2 rounded-full text-muted-foreground/40 hover:text-foreground hover:bg-secondary/80 transition-colors"
          title="Rafraîchir"
        >
          <RefreshCw className="h-4 w-4" />
        </button>
        {activeRoll && (
          <Dice3DOverlay rollData={activeRoll} onComplete={clearActiveRoll} />
        )}
      </div>
    );
  }

  return (
    <div 
      ref={containerRef}
      className="min-h-screen bg-background select-none overflow-hidden"
      data-testid="player-map-view"
    >
      {/* Header UI */}
      {showUI && (
        <div className="absolute top-0 left-0 right-0 z-50 p-4 flex justify-between items-start pointer-events-none">
          {/* Map info */}
          <div className="pointer-events-auto">
            <Badge variant="secondary" className="bg-background/90 backdrop-blur-sm shadow-lg text-base px-4 py-2">
              <MapIcon className="h-4 w-4 mr-2" />
              {map.name}
              {syncEnabled && (
                <span className="ml-2 flex items-center gap-1 text-primary">
                  <RefreshCw className="h-3 w-3 animate-spin" />
                  Sync
                </span>
              )}
            </Badge>
          </div>
          
          {/* Controls */}
          <div className="flex items-center gap-2 pointer-events-auto">
            {/* Map navigation */}
            {availableMaps.length > 1 && (
              <Button 
                variant="secondary" 
                size="icon"
                className="bg-background/90 backdrop-blur-sm shadow-lg"
                onClick={() => setShowMapList(!showMapList)}
              >
                <Compass className="h-4 w-4" />
              </Button>
            )}
            
            {/* Zoom controls */}
            <div className="flex items-center gap-1 bg-background/90 backdrop-blur-sm rounded-lg shadow-lg p-1">
              <Button 
                variant="ghost" 
                size="icon" 
                className="h-8 w-8"
                onClick={() => !syncEnabled && setZoom(z => Math.min(3, z + 0.2))}
                disabled={syncEnabled}
              >
                <ZoomIn className="h-4 w-4" />
              </Button>
              <span className="text-xs px-2 min-w-[3rem] text-center">{Math.round(zoom * 100)}%</span>
              <Button 
                variant="ghost" 
                size="icon" 
                className="h-8 w-8"
                onClick={() => !syncEnabled && setZoom(z => Math.max(0.5, z - 0.2))}
                disabled={syncEnabled}
              >
                <ZoomOut className="h-4 w-4" />
              </Button>
            </div>
            
            {/* Recenter */}
            <Button 
              variant="secondary" 
              size="icon"
              className="bg-background/90 backdrop-blur-sm shadow-lg"
              onClick={recenter}
              disabled={syncEnabled}
            >
              <Home className="h-4 w-4" />
            </Button>
            
            {/* Fullscreen */}
            <Button 
              variant="secondary" 
              size="icon"
              className="bg-background/90 backdrop-blur-sm shadow-lg"
              onClick={toggleFullscreen}
            >
              {isFullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
            </Button>
            
            {/* Toggle UI */}
            <Button 
              variant="secondary" 
              size="icon"
              className="bg-background/90 backdrop-blur-sm shadow-lg"
              onClick={() => setShowUI(false)}
            >
              <Eye className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Hidden UI restore button */}
      {!showUI && (
        <Button
          variant="ghost"
          size="icon"
          className="absolute top-4 right-4 z-50 opacity-30 hover:opacity-100"
          onClick={() => setShowUI(true)}
        >
          <Eye className="h-4 w-4" />
        </Button>
      )}

      {/* Always visible manual refresh button */}
      <Button
        variant="secondary"
        size="icon"
        className="absolute top-14 right-4 z-50 bg-background/90 backdrop-blur-sm shadow-lg"
        onClick={reloadPlayerDisplay}
        title="Recharger l'affichage"
      >
        <RefreshCw className="h-4 w-4" />
      </Button>

      {/* Map list dropdown */}
      {showMapList && showUI && (
        <div className="absolute top-16 right-4 z-50">
          <Card className="w-64 shadow-lg">
            <CardHeader className="py-3">
              <CardTitle className="text-sm flex items-center gap-2">
                <Compass className="h-4 w-4" />
                Cartes disponibles
              </CardTitle>
            </CardHeader>
            <CardContent className="p-2">
              <ScrollArea className="max-h-48">
                {availableMaps.map(m => (
                  <div 
                    key={m.id}
                    className={cn(
                      "flex items-center gap-2 p-2 rounded cursor-pointer transition-colors",
                      m.id === map.id ? "bg-primary/20" : "hover:bg-secondary"
                    )}
                    onClick={() => {
                      if (m.id !== map.id) {
                        toast.info('Demandez au MJ d\'ouvrir cette carte');
                      }
                      setShowMapList(false);
                    }}
                  >
                    <MapIcon className="h-4 w-4 text-muted-foreground" />
                    <span className="flex-1 truncate">{m.name}</span>
                    {m.id === map.id && (
                      <Badge variant="secondary" className="text-xs">Actuelle</Badge>
                    )}
                  </div>
                ))}
              </ScrollArea>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Map container */}
      <div
        className={cn(
          "w-full h-screen flex items-center justify-center",
          isDragging && "cursor-grabbing",
          !isDragging && !syncEnabled && "cursor-grab",
          syncEnabled && "cursor-default"
        )}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={() => setIsDragging(false)}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <div
          style={{
            transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
            transformOrigin: 'center',
            transition: isDragging ? 'none' : 'transform 0.15s ease-out'
          }}
          className="relative"
        >
          <img 
            src={map.image_url} 
            alt={map.name}
            className="max-w-[90vw] max-h-[90vh] object-contain"
            draggable={false}
          />
          
          {/* Pins */}
          {pins.map(pin => {
            const Icon = getPinIcon(pin.pin_type);
            const color = getPinColor(pin.pin_type);
            
            return (
              <div
                key={pin.id}
                className="absolute cursor-pointer transform -translate-x-1/2 -translate-y-full transition-transform hover:scale-110"
                style={{
                  left: `${pin.x * 100}%`,
                  top: `${pin.y * 100}%`
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  handlePinClick(pin);
                }}
                data-testid={`pin-${pin.id}`}
              >
                <div 
                  className="relative flex flex-col items-center"
                  style={{ filter: `drop-shadow(0 2px 4px ${color}40)` }}
                >
                  <div 
                    className="p-2 rounded-full shadow-lg"
                    style={{ backgroundColor: color }}
                  >
                    <Icon className="h-5 w-5 text-white" />
                  </div>
                  <div 
                    className="absolute -bottom-1 w-2 h-2 rotate-45"
                    style={{ backgroundColor: color }}
                  />
                  {/* Pin title on hover */}
                  <div className="absolute top-full mt-2 px-2 py-1 bg-background/95 rounded shadow-lg text-xs font-medium whitespace-nowrap opacity-0 hover:opacity-100 pointer-events-none">
                    {pin.title}
                  </div>
                </div>
                
                {/* Portal indicator */}
                {pin.linked_map_id && (
                  <div className="absolute -top-1 -right-1 bg-primary rounded-full p-0.5">
                    <Navigation className="h-3 w-3 text-primary-foreground" />
                  </div>
                )}
              </div>
            );
          })}
          
          {/* Tactical Grid Overlay (rendered before fog so fog covers grid in unrevealed areas) */}
          {gridEnabled && map && (
            <TacticalGridOverlay
              width={map.width || 1920}
              height={map.height || 1080}
              enabled={gridEnabled}
              settings={gridSettings}
            />
          )}

          {/* Fog of War Overlay */}
          {fogEnabled && (
            <FogOverlay
              width={map.width || 1920}
              height={map.height || 1080}
              revealedZones={fogRevealedZones}
              enabled={fogEnabled}
              settings={fogSettings}
              className="rounded-lg"
            />
          )}
        </div>
      </div>

      {/* Pin Detail Dialog */}
      <Dialog open={pinDetailOpen} onOpenChange={setPinDetailOpen}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedPin && (
                <>
                  {(() => {
                    const Icon = getPinIcon(selectedPin.pin_type);
                    const color = getPinColor(selectedPin.pin_type);
                    return (
                      <div className="p-1.5 rounded" style={{ backgroundColor: `${color}20` }}>
                        <Icon className="h-5 w-5" style={{ color }} />
                      </div>
                    );
                  })()}
                  {pinDetail?.title || selectedPin.title}
                </>
              )}
            </DialogTitle>
          </DialogHeader>
          
          {loadingDetail ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
          ) : pinDetail && (
            <div className="space-y-4">
              {/* Description */}
              {pinDetail.public_description && (
                <p className="text-muted-foreground">{pinDetail.public_description}</p>
              )}
              
              {/* Portal link */}
              {pinDetail.linked_map_id && pinDetail.linked_map_name && (
                <Card className="bg-primary/5 border-primary/20">
                  <CardContent className="py-3 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Navigation className="h-4 w-4 text-primary" />
                      <span className="text-sm">Mene vers : <strong>{pinDetail.linked_map_name}</strong></span>
                    </div>
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => handleNavigateToMap(pinDetail.linked_map_id)}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </CardContent>
                </Card>
              )}
              
              {/* Linked entities */}
              {pinDetail.linked && (
                <div className="space-y-3">
                  {/* Quests */}
                  {pinDetail.linked.quests?.length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium flex items-center gap-2 mb-2">
                        <Scroll className="h-4 w-4 text-amber-500" />
                        Quetes liees
                      </h4>
                      <div className="space-y-1">
                        {pinDetail.linked.quests.map(quest => (
                          <div key={quest.id} className="text-sm flex items-center gap-2 p-2 bg-secondary/30 rounded">
                            <Star className="h-3 w-3 text-amber-500" />
                            <span>{quest.title}</span>
                            <Badge variant="outline" className="text-xs ml-auto">{quest.status}</Badge>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* NPCs */}
                  {pinDetail.linked.npcs?.length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium flex items-center gap-2 mb-2">
                        <Users className="h-4 w-4 text-blue-500" />
                        PNJ presents
                      </h4>
                      <div className="space-y-1">
                        {pinDetail.linked.npcs.map(npc => (
                          <div key={npc.id} className="text-sm flex items-center gap-2 p-2 bg-secondary/30 rounded">
                            <Users className="h-3 w-3 text-blue-500" />
                            <span>{npc.name}</span>
                            {npc.role && <span className="text-muted-foreground">({npc.role})</span>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Lore */}
                  {pinDetail.linked.lore?.length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium flex items-center gap-2 mb-2">
                        <Book className="h-4 w-4 text-purple-500" />
                        Lore associe
                      </h4>
                      <div className="space-y-1">
                        {pinDetail.linked.lore.map(article => (
                          <div key={article.id} className="text-sm p-2 bg-secondary/30 rounded">
                            <div className="flex items-center gap-2">
                              <Book className="h-3 w-3 text-purple-500" />
                              <span className="font-medium">{article.title}</span>
                            </div>
                            {article.summary && (
                              <p className="text-xs text-muted-foreground mt-1 ml-5">{article.summary}</p>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
      {activeRoll && (
        <Dice3DOverlay rollData={activeRoll} onComplete={clearActiveRoll} />
      )}
    </div>
  );
}

